/**
 * Страница записей на приём: просмотр всех записей со статусами и уведомлениями
 * Дата: 30.01.2026 - Создано для TODO-046 (Страница записей и доработка аналитики)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/appointments.ts - API для работы с записями
 * - internal/handler/appointment.go - бэкенд обработчик записей
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/FilterSelect.tsx - унифицированный фильтр
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/DateRangePicker.tsx - выбор периода
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * 
 * КАК РАБОТАЕТ:
 * 1. Загружает список записей через appointmentsApi.list()
 * 2. Фильтрует по датам, статусу и поисковому запросу
 * 3. Отображает таблицу с записями, статусами и уведомлениями
 * 4. Кнопка "Чат" переходит в /messages?phone={phone}
 * 
 * СХЕМА:
 * AppointmentsPage → appointmentsApi.list() → backend API
 * AppointmentsPage → SearchInput → фильтрация по ФИО/телефону
 * AppointmentsPage → FilterSelect → фильтрация по статусу
 * AppointmentsPage → DateRangePicker → фильтрация по периоду
 * AppointmentsPage → navigate('/messages?phone={phone}') → MessagesPage
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { 
  Card, 
  CardContent, 
  Button, 
  Badge, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableHeader, 
  TableCell, 
  TenantSelector,
  SearchInput,
  FilterSelect,
  DateRangePicker,
  EmptyState,
  LoadingState,
  Pagination,
} from '@/components/ui';
import { appointmentsApi, AppointmentDTO, AppointmentFilters } from '@/api/appointments';
import { useTenantScope } from '@/hooks/useTenantScope';
import { formatDateTime, formatPhone } from '@/utils/formatters';
import { format, startOfDay, endOfDay, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { MessageSquare, Check, X, Clock, AlertCircle } from 'lucide-react';
import { DependencyStrip } from '@/features/monitoring/DependencyStrip';
import { FlowNote } from '@/features/monitoring/FlowNote';

// Маппинг статусов на цвета и иконки
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  scheduled: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" />, label: 'Запланировано' },
  confirmed: { color: 'bg-green-100 text-green-800', icon: <Check className="w-3 h-3" />, label: 'Подтверждено' },
  in_progress: { color: 'bg-blue-100 text-blue-800', icon: <Clock className="w-3 h-3" />, label: 'В процессе' },
  completed: { color: 'bg-green-100 text-green-800', icon: <Check className="w-3 h-3" />, label: 'Завершено' },
  cancelled: { color: 'bg-red-100 text-red-800', icon: <X className="w-3 h-3" />, label: 'Отменено' },
  no_show: { color: 'bg-gray-100 text-gray-800', icon: <X className="w-3 h-3" />, label: 'Не явился' },
  unknown: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="w-3 h-3" />, label: 'Неизвестен' },
};

export function AppointmentsPage() {
  const navigate = useNavigate();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  
  const [appointments, setAppointments] = useState<AppointmentDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'appointments' });
  }, []);

  // Загрузка записей
  useEffect(() => {
    if (!tenantId) {
      setAppointments([]);
      return;
    }

    const loadAppointments = async () => {
      setIsLoading(true);
      try {
        const filters: AppointmentFilters = {
          tenant_id: tenantId,
          from_date: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
          to_date: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
          status: statusFilter,
          search: searchQuery || undefined,
          limit,
          offset,
        };

        const response = await appointmentsApi.list(filters);
        setAppointments(response.appointments);
        setTotal(response.pagination.total);
        logger.info('appointments_loaded', { 
          count: response.appointments.length,
          total: response.pagination.total,
          filters 
        });
      } catch (error: any) {
        logger.error('appointments_load_failed', { 
          error: error.response?.data?.error || error.message 
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadAppointments();
  }, [tenantId, dateFrom, dateTo, statusFilter, searchQuery, limit, offset]);

  // Переход в чат
  const handleOpenChat = (phone: string) => {
    logger.info('appointment_chat_click', { phone });
    navigate(`/messages?phone=${encodeURIComponent(phone)}`);
  };

  // Получить статус badge
  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.unknown;
    return (
      <Badge className={`${config.color} inline-flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  if (requireTenantSelection) {
    return (
      <MainLayout headerTitle="Записи на приём" disableMainScroll>
        <LinearScreen
          eyebrow="Приёмы"
          title="Записи на приём"
          lead="Выберите клинику, чтобы увидеть записи и статусы уведомлений."
          toolbar={
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                required={false}
                showLabel={false}
              />
            </div>
          }
        >
          <EmptyState
            title="Выберите клинику"
            description="Выберите клинику для просмотра записей"
          />
        </LinearScreen>
      </MainLayout>
    );
  }

  return (
    <MainLayout headerTitle="Записи на приём" disableMainScroll>
      <LinearScreen
        eyebrow="Приёмы"
        title="Записи на приём"
        lead="Записи выбранной клиники: статус приёма и ушло ли уведомление."
        toolbar={
          <div className="space-y-3">
            {isSuperAdmin && (
              <div className="max-w-xs">
                <TenantSelector
                  value={selectedTenantId || ''}
                  onChange={setSelectedTenant}
                  required={false}
                  showLabel={false}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <div className="min-w-[240px] flex-1">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Поиск по ФИО или телефону..."
                  testId="appointments-search"
                />
              </div>
              <div className="w-48">
                <FilterSelect
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: 'all', label: 'Все статусы' },
                    { value: 'scheduled', label: 'Запланировано' },
                    { value: 'confirmed', label: 'Подтверждено' },
                    { value: 'in_progress', label: 'В процессе' },
                    { value: 'completed', label: 'Завершено' },
                    { value: 'cancelled', label: 'Отменено' },
                    { value: 'no_show', label: 'Не явился' },
                    { value: 'unknown', label: 'Неизвестен' },
                  ]}
                  testId="appointments-status-filter"
                />
              </div>
            </div>
            <DateRangePicker
              from={dateFrom}
              to={dateTo}
              onChange={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
                setOffset(0);
              }}
              presets={[
                { label: 'Сегодня', from: startOfDay(new Date()), to: endOfDay(new Date()) },
                { label: 'Завтра', from: startOfDay(addDays(new Date(), 1)), to: endOfDay(addDays(new Date(), 1)) },
                { label: 'Неделя', from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: endOfWeek(new Date(), { weekStartsOn: 1 }) },
                { label: 'Месяц', from: startOfMonth(new Date()), to: endOfMonth(new Date()) },
              ]}
              testId="appointments-date-range"
            />
          </div>
        }
      >
        <div className="mb-4 space-y-3">
          <FlowNote id="appointments" />
          <DependencyStrip edge="data_layer" />
        </div>
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <LoadingState text="Загрузка записей..." />
            ) : appointments.length === 0 ? (
              <EmptyState
                title="Записи не найдены"
                description="Попробуйте изменить фильтры или период"
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Время</TableHead>
                      <TableHead>Пациент</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead>Врач</TableHead>
                      <TableHead>Услуга</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Уведомления</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {appointments.map((apt) => (
                      <TableRow key={apt.id}>
                        <TableCell>{formatDateTime(apt.appointment_datetime)}</TableCell>
                        <TableCell>{apt.patient_name || '—'}</TableCell>
                        <TableCell>{formatPhone(apt.phone)}</TableCell>
                        <TableCell>{apt.doctor_name || '—'}</TableCell>
                        <TableCell>{apt.service_name || '—'}</TableCell>
                        <TableCell>{getStatusBadge(apt.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2 text-sm">
                            <span className={apt.reminder_24h_sent ? 'text-green-600' : 'text-gray-400'}>
                              24h {apt.reminder_24h_sent ? '✅' : '❌'}
                            </span>
                            <span className={apt.reminder_2h_sent ? 'text-green-600' : 'text-gray-400'}>
                              2h {apt.reminder_2h_sent ? '✅' : '❌'}
                            </span>
                            {apt.status === 'completed' && (
                              <span className={apt.feedback_sent ? 'text-green-600' : 'text-gray-400'}>
                                ОС {apt.feedback_sent ? '✅' : '❌'}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenChat(apt.phone)}
                            testId={`appointment-chat-${apt.id}`}
                            className="h-8 px-2 text-xs"
                          >
                            <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                            Чат
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Дата: 10.02.2026 - Refactored: используем переиспользуемый Pagination */}
                <Pagination
                  offset={offset}
                  limit={limit}
                  total={total}
                  onOffsetChange={setOffset}
                  className="mt-4"
                  testId="appointments-pagination"
                />
              </>
            )}
          </CardContent>
        </Card>
      </LinearScreen>
    </MainLayout>
  );
}
